/*
 * Proprietary and confidential | 2023 | SoundTalks NV
 */

import React, { useState, lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router';
import { Helmet } from 'react-helmet-async';
import { isIE } from 'react-device-detect';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNetworkStatus } from 'utils/hooks';

import { baseAppPath, baseAdminPath, signInPath, notFoundPath, tooManyRequestsErrorPath } from 'constants/urls';

import { adminAccessRoles } from 'constants/app';

import { withAuth, Sentry } from 'data';
import colors from 'ui/styles/colors.module.scss';
import AppShared from 'containers/shared';
import Modal from 'components/core/modal';
import ApiError from 'components/core/api-error';
import { FluidPreloader } from 'components/core/preloader';

const AppRoot = lazy(() => import('containers/app'));

const AdminRoot = lazy(() => import('containers/admin'));

const AuthorizedAppRoute = withAuth({ redirectTo: signInPath })(AppRoot);
const AuthorizedAdminRoute = withAuth({ redirectTo: signInPath, accessRoles: adminAccessRoles })(AdminRoot);

const Soundtalks = () => {
  const [isModalOpen, setModalOpen] = useState(window.navigator.userAgent.includes('MSIE') || isIE);

  const closeModal = () => {
    setModalOpen(false);
  };

  useNetworkStatus();

  return (
    <React.Fragment>
      <Helmet titleTemplate="%s | My Soundtalks">
        <meta name="theme-color" content={colors.primary} />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      </Helmet>
      <Modal
        show={isModalOpen}
        closeButtonText="Ok"
        title={<span>Attention, the use of Internet Explorer is not supported</span>}
        onClose={closeModal}
        content={
          <div>
            <span>
              Sorry, the use of the application in Internet Explorer is no longer supported and therefore it is possible
              that the user experience is not optimal. We recommend to use another browser like Google Chrome or Edge.
              SoundTalks took the decision to abandon the support of Internet Explorer after Microsoft launched
              Microsoft Edge as new browser and decided to stop support for Internet Explorer in 2016.
            </span>
          </div>
        }
      />
      <ToastContainer />

      <Suspense fallback={<FluidPreloader style={{ height: '100vh' }} />}>
        <Routes>
          <Route path={`${baseAppPath}/*`} element={<AuthorizedAppRoute />} />
          <Route path={`${baseAdminPath}/*`} element={<AuthorizedAdminRoute />} />
          <Route path={tooManyRequestsErrorPath} element={<ApiError />} />
          <Route path={notFoundPath} element={<ApiError />} />
          <Route path="*" element={<AppShared />} />
        </Routes>
      </Suspense>
    </React.Fragment>
  );
};

export default Sentry.withProfiler(Soundtalks);
